import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon, 
  CalendarDaysIcon,
  UserIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EnvelopeIcon,
  PhoneIcon,
  XCircleIcon,
  BellIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UserAvatar';

interface Booking {
  id: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  totalPrice: number;
  notes?: string;
  rejectionReason?: string;
  createdAt: Date;
  updatedAt: Date;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    avatar?: string;
    username?: string;
    status?: string;
    createdAt?: Date;
  };
  course: {
    id: string;
    title: string;
    subject: string;
  };
}

const TeacherBookingsImproved = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [processingBatch, setProcessingBatch] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Booking | null>(null);
  const [showStudentDetail, setShowStudentDetail] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingBooking, setRejectingBooking] = useState<Booking | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // 新增：可视性增强状态
  const [viewedBookings, setViewedBookings] = useState<Set<string>>(new Set());
  const [newBookingNotifications, setNewBookingNotifications] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 判断预约是否为新的（24小时内创建）
  const isNewBooking = (booking: Booking) => {
    if (viewedBookings.has(booking.id)) return false;
    
    const now = new Date().getTime();
    const createdAt = new Date(booking.createdAt).getTime();
    const hoursDiff = (now - createdAt) / (1000 * 3600);
    
    return hoursDiff <= 24 && booking.status === 'pending';
  };

  // 获取各状态的统计数据
  const getBookingStats = () => {
    const stats = {
      total: bookings.length,
      pending: bookings.filter(b => b.status === 'pending').length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      completed: bookings.filter(b => b.status === 'completed').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
      newPending: bookings.filter(b => isNewBooking(b)).length,
      todayBookings: bookings.filter(b => {
        const today = new Date();
        const bookingDate = new Date(b.date);
        return bookingDate.toDateString() === today.toDateString();
      }).length
    };
    return stats;
  };

  // 自动标记预约为已查看
  const markAsViewed = useCallback((bookingId: string) => {
    setViewedBookings(prev => new Set(prev).add(bookingId));
    setNewBookingNotifications(prev => {
      const newSet = new Set(prev);
      newSet.delete(bookingId);
      return newSet;
    });
  }, []);

  // 预约卡片引用回调
  const bookingCardRef = useCallback((node: HTMLDivElement | null, bookingId: string) => {
    if (node && observerRef.current) {
      node.setAttribute('data-booking-id', bookingId);
      observerRef.current.observe(node);
    }
  }, []);

  // 设置Intersection Observer
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const bookingId = entry.target.getAttribute('data-booking-id');
          if (!bookingId) return;

          if (entry.isIntersecting) {
            // 预约卡片进入视口，2秒后标记为已查看
            setTimeout(() => {
              const booking = bookings.find(b => b.id === bookingId);
              if (booking && isNewBooking(booking)) {
                markAsViewed(bookingId);
              }
            }, 2000);
          }
        });
      },
      {
        threshold: 0.7,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [bookings, markAsViewed]);

  // 初始化新预约通知
  useEffect(() => {
    const newBookings = bookings
      .filter(booking => isNewBooking(booking))
      .map(booking => booking.id);
    
    setNewBookingNotifications(new Set(newBookings));
  }, [bookings]);

  // 获取预约列表
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/bookings/teacher', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data);
        setFilteredBookings(data);
      } else {
        toast.error('获取预约列表失败');
      }
    } catch (error) {
      console.error('获取预约失败:', error);
      toast.error('获取预约列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // 筛选逻辑
  useEffect(() => {
    let filtered = [...bookings];

    // 搜索筛选
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.course.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 状态筛选
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // 日期筛选
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.date);
        const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
        
        switch (dateFilter) {
          case 'today':
            return bookingDay.getTime() === today.getTime();
          case 'week':
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return bookingDay >= weekStart && bookingDay <= weekEnd;
          case 'month':
            return bookingDay.getMonth() === today.getMonth() && 
                   bookingDay.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }

    setFilteredBookings(filtered);
  }, [bookings, searchTerm, statusFilter, dateFilter]);

  // 查看学生详情
  const viewStudentDetail = (booking: Booking) => {
    setSelectedStudent(booking);
    setShowStudentDetail(true);
  };

  // 显示拒绝模态框
  const showRejectConfirm = (booking: Booking) => {
    setRejectingBooking(booking);
    setRejectReason('');
    setShowRejectModal(true);
  };

  // 单个预约操作
  const handleBookingAction = async (bookingId: string, action: 'confirm' | 'cancel', reason?: string) => {
    try {
      const token = localStorage.getItem('token');
      const url = action === 'confirm' 
        ? `/api/bookings/${bookingId}/confirm`
        : `/api/bookings/${bookingId}/cancel`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: action === 'cancel' ? JSON.stringify({ 
          reason: reason || '教师取消了此预约' 
        }) : undefined
      });

      if (response.ok) {
        await fetchBookings();
        toast.success(action === 'confirm' ? '预约确认成功' : '预约已拒绝');
        
        // 关闭拒绝模态框
        if (action === 'cancel') {
          setShowRejectModal(false);
          setRejectingBooking(null);
          setRejectReason('');
        }
      } else {
        const error = await response.json();
        toast.error(error.message || `${action === 'confirm' ? '确认' : '拒绝'}失败`);
      }
    } catch (error) {
      console.error(`${action}预约失败:`, error);
      toast.error(`${action === 'confirm' ? '确认' : '拒绝'}预约失败`);
    }
  };

  // 确认拒绝预约
  const confirmReject = () => {
    if (!rejectingBooking) return;
    
    if (!rejectReason.trim()) {
      toast.error('请输入拒绝理由');
      return;
    }

    handleBookingAction(rejectingBooking.id, 'cancel', rejectReason);
  };

  // 批量操作
  const handleBatchAction = async (action: 'confirm' | 'cancel') => {
    if (selectedBookings.length === 0) {
      toast.error('请先选择要操作的预约');
      return;
    }

    setProcessingBatch(true);
    try {
      const promises = selectedBookings.map(bookingId => 
        handleBookingAction(bookingId, action)
      );
      
      await Promise.all(promises);
      setSelectedBookings([]);
      toast.success(`批量${action === 'confirm' ? '确认' : '取消'}成功`);
    } catch (error) {
      toast.error(`批量操作失败`);
    } finally {
      setProcessingBatch(false);
    }
  };

  // 选择/取消选择预约
  const toggleBookingSelection = (bookingId: string) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId)
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    const pendingBookings = filteredBookings.filter(b => b.status === 'pending');
    if (selectedBookings.length === pendingBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(pendingBookings.map(b => b.id));
    }
  };

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    const statusMap = {
      pending: '待确认',
      confirmed: '已确认',
      completed: '已完成',
      cancelled: '已取消'
    };
    return statusMap[status as keyof typeof statusMap] || '未知';
  };

  const stats = getBookingStats();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              预约管理
              {stats.newPending > 0 && (
                <span className="ml-3 bg-red-500 text-white text-sm px-2 py-1 rounded-full animate-pulse">
                  {stats.newPending} 个新预约
                </span>
              )}
            </h1>
            <p className="mt-2 text-gray-600">
              管理学生的课程预约，今日有 {stats.todayBookings} 个预约
            </p>
          </div>
          
          {/* 新预约提醒卡片 */}
          {stats.newPending > 0 && (
            <div className="mt-4 lg:mt-0 bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <BellIcon className="h-6 w-6 text-orange-500 animate-bounce" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">有新的预约请求</h3>
                  <div className="mt-1 text-sm text-orange-600">
                    <p>🔔 {stats.newPending} 个学生预约等待您确认</p>
                    <p className="text-xs mt-1 opacity-75">滚动查看详情，系统将自动标记已查看</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 增强统计卡片网格 */}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {/* 总预约数 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">总预约</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          {/* 待确认预约 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-sm relative">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-900">待确认</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              </div>
            </div>
            {/* 新预约红点 */}
            {stats.newPending > 0 && (
              <div className="absolute -top-1 -right-1 flex items-center">
                <div className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                  {stats.newPending}
                </div>
              </div>
            )}
          </div>

          {/* 已确认预约 */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">已确认</p>
                <p className="text-2xl font-bold text-green-700">{stats.confirmed}</p>
              </div>
            </div>
          </div>

          {/* 已完成预约 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <CheckCircleSolid className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-900">已完成</p>
                <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
              </div>
            </div>
          </div>

          {/* 已取消预约 */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <XCircleIcon className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-900">已取消</p>
                <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
              </div>
            </div>
          </div>

          {/* 今日预约 */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <CalendarDaysIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-900">今日预约</p>
                <p className="text-2xl font-bold text-purple-700">{stats.todayBookings}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* 搜索框 */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索学生姓名或课程..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 筛选按钮和批量操作 */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              筛选
            </button>

            {/* 批量操作 */}
            {selectedBookings.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  已选择 {selectedBookings.length} 个预约
                </span>
                <button
                  onClick={() => handleBatchAction('confirm')}
                  disabled={processingBatch}
                  className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  批量确认
                </button>
                <button
                  onClick={() => handleBatchAction('cancel')}
                  disabled={processingBatch}
                  className="flex items-center px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  批量取消
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 展开的筛选选项 */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* 状态筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">全部状态</option>
                  <option value="pending">待确认</option>
                  <option value="confirmed">已确认</option>
                  <option value="completed">已完成</option>
                  <option value="cancelled">已取消</option>
                </select>
              </div>

              {/* 日期筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">日期</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">全部日期</option>
                  <option value="today">今天</option>
                  <option value="week">本周</option>
                  <option value="month">本月</option>
                </select>
              </div>

              {/* 清除筛选 */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                    setShowFilters(false);
                  }}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  清除筛选
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 预约列表 */}
      {filteredBookings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无预约记录</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' 
              ? '没有符合筛选条件的预约' 
              : '还没有学生预约您的课程'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* 表格头部 */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedBookings.length === filteredBookings.filter(b => b.status === 'pending').length && selectedBookings.length > 0}
                onChange={toggleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm font-medium text-gray-900">全选</span>
            </div>
          </div>

          {/* 预约列表 */}
          <div className="divide-y divide-gray-200">
            {filteredBookings.map((booking) => {
              const isNew = isNewBooking(booking);
              
              return (
                <div 
                  key={booking.id} 
                  ref={(node) => bookingCardRef(node, booking.id)}
                  className={`p-6 hover:bg-gray-50 transition-colors relative ${
                    isNew ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  {/* 新预约标识 */}
                  {isNew && (
                    <div className="absolute top-4 right-4 flex items-center space-x-2">
                      <div className="animate-pulse">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      </div>
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                        新预约
                      </span>
                    </div>
                  )}

                  <div className="flex items-start space-x-4">
                    {/* 选择框 */}
                    {booking.status === 'pending' && (
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => toggleBookingSelection(booking.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    )}

                    {/* 学生头像 */}
                    <div className="flex-shrink-0 relative">
                      <UserAvatar
                        avatar={booking.student?.avatar}
                        firstName={booking.student?.firstName}
                        lastName={booking.student?.lastName}
                        size="lg"
                      />
                      {/* 新学生红点 */}
                      {isNew && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                      )}
                    </div>

                    {/* 预约信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-medium text-gray-900 flex items-center">
                            {booking.student.firstName} {booking.student.lastName}
                            {isNew && (
                              <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                                刚刚预约
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">{booking.student.email}</p>
                        </div>
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyle(booking.status)}`}>
                          {getStatusText(booking.status)}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="flex items-center">
                          <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{booking.course.title}</p>
                            <p className="text-xs text-gray-500">{booking.course.subject}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(booking.date).toLocaleDateString('zh-CN')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {booking.startTime} - {booking.endTime}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{booking.duration}小时</p>
                            <p className="text-xs text-gray-500">¥{booking.totalPrice}</p>
                          </div>
                        </div>
                      </div>

                      {/* 新预约额外信息 */}
                      {isNew && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center">
                            <BellIcon className="h-4 w-4 text-blue-600 mr-2" />
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">新预约请求</span> - 
                              {(() => {
                                const now = new Date().getTime();
                                const createdAt = new Date(booking.createdAt).getTime();
                                const hoursDiff = Math.floor((now - createdAt) / (1000 * 3600));
                                const minutesDiff = Math.floor((now - createdAt) / (1000 * 60));
                                
                                if (hoursDiff >= 1) {
                                  return ` ${hoursDiff}小时前提交`;
                                } else {
                                  return ` ${minutesDiff}分钟前提交`;
                                }
                              })()}
                            </p>
                          </div>
                        </div>
                      )}

                      {booking.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">备注：</span>{booking.notes}
                          </p>
                        </div>
                      )}

                      {/* 操作按钮 */}
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          onClick={() => viewStudentDetail(booking)}
                          className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          查看学生详情
                        </button>

                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleBookingAction(booking.id, 'confirm')}
                              className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <CheckIcon className="h-4 w-4 mr-1" />
                              确认预约
                            </button>
                            <button
                              onClick={() => showRejectConfirm(booking)}
                              className="flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <XMarkIcon className="h-4 w-4 mr-1" />
                              拒绝预约
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 学生详情模态框 */}
      {showStudentDetail && selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* 模态框头部 */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">学生详细信息</h3>
                <button
                  onClick={() => setShowStudentDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              {/* 学生信息 */}
              <div className="py-6">
                <div className="flex items-center space-x-6 mb-6">
                  <UserAvatar
                    avatar={selectedStudent.student?.avatar}
                    firstName={selectedStudent.student?.firstName}
                    lastName={selectedStudent.student?.lastName}
                    size="xl"
                  />
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">
                      {selectedStudent.student.firstName} {selectedStudent.student.lastName}
                    </h4>
                    <p className="text-gray-500">
                      {selectedStudent.student.username || '暂无用户名'}
                    </p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      selectedStudent.student.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedStudent.student.status === 'active' ? '活跃用户' : '普通用户'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">邮箱</p>
                        <p className="text-sm text-gray-500">{selectedStudent.student.email}</p>
                      </div>
                    </div>

                    {selectedStudent.student.phone && (
                      <div className="flex items-center">
                        <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">电话</p>
                          <p className="text-sm text-gray-500">{selectedStudent.student.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">注册时间</p>
                        <p className="text-sm text-gray-500">
                          {selectedStudent.student.createdAt 
                            ? new Date(selectedStudent.student.createdAt).toLocaleDateString('zh-CN')
                            : '暂无记录'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">当前预约课程</p>
                        <p className="text-sm text-gray-500">{selectedStudent.course.title}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 预约详情 */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h5 className="text-lg font-medium text-gray-900 mb-4">本次预约详情</h5>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">预约时间</p>
                        <p className="text-sm text-gray-500">
                          {new Date(selectedStudent.date).toLocaleDateString('zh-CN')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedStudent.startTime} - {selectedStudent.endTime}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">课程时长</p>
                        <p className="text-sm text-gray-500">{selectedStudent.duration} 小时</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">费用</p>
                        <p className="text-sm text-gray-500">¥{selectedStudent.totalPrice}</p>
                      </div>
                    </div>
                    {selectedStudent.notes && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-900">学生备注</p>
                        <p className="text-sm text-gray-500 mt-1">{selectedStudent.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 模态框底部操作 */}
              <div className="flex items-center justify-end pt-4 border-t border-gray-200 space-x-3">
                <button
                  onClick={() => setShowStudentDetail(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  关闭
                </button>
                {selectedStudent.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        setShowStudentDetail(false);
                        handleBookingAction(selectedStudent.id, 'confirm');
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                    >
                      确认预约
                    </button>
                    <button
                      onClick={() => {
                        setShowStudentDetail(false);
                        showRejectConfirm(selectedStudent);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                    >
                      拒绝预约
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 拒绝理由模态框 */}
      {showRejectModal && rejectingBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* 模态框头部 */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">拒绝预约</h3>
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              {/* 学生信息 */}
              <div className="py-4">
                <div className="flex items-center space-x-3 mb-4">
                  <UserAvatar
                    avatar={rejectingBooking.student?.avatar}
                    firstName={rejectingBooking.student?.firstName}
                    lastName={rejectingBooking.student?.lastName}
                    size="md"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {rejectingBooking.student.firstName} {rejectingBooking.student.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{rejectingBooking.course.title}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="rejectReason" className="block text-sm font-medium text-gray-700 mb-2">
                    拒绝理由 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="rejectReason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="请详细说明拒绝的原因，这将发送给学生..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                    <p className="text-sm text-yellow-700">
                      拒绝理由将会发送给学生，请确保理由充分且礼貌。
                    </p>
                  </div>
                </div>
              </div>

              {/* 模态框底部操作 */}
              <div className="flex items-center justify-end pt-4 border-t border-gray-200 space-x-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={confirmReject}
                  disabled={!rejectReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  确认拒绝
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherBookingsImproved; 